import React from "react";
import styles from "./Calculator.module.css";

export type CalculatorSliderFieldProps = {
  label: string;
  tooltip: string;
  value: number;
  min: number;
  max: number;
  step: number;
  prefix?: string;
  suffix?: string;
  minLabel: string;
  maxLabel: string;
  onChange: (_value: number) => void;
};

export const CalculatorSliderField: React.FC<CalculatorSliderFieldProps> = ({
  label,
  tooltip,
  value,
  min,
  max,
  step,
  prefix,
  suffix,
  minLabel,
  maxLabel,
  onChange,
}) => {
  const fillPercentage = ((value - min) / (max - min)) * 100;

  const handleTextInput = (rawText: string) => {
    const parsedValue = parseFloat(rawText);
    if (!isNaN(parsedValue)) onChange(Math.min(Math.max(parsedValue, min), max));
  };

  return (
    <div className="mb-3">
      <label className="text-[11px] mb-1.25 flex items-center gap-1 font-bold text-[#3d4f6e]">
        {label}
        <span data-tip={tooltip} className={styles.tooltip}>?</span>
      </label>

      {prefix ? (
        <div className="relative w-full">
          <span className="pointer-events-none absolute top-1/2 left-2.25 z-2 -translate-y-1/2 text-xs leading-none font-bold text-[#9aaac0]">
            {prefix}
          </span>
          <input
            type="number"
            value={value}
            step={step}
            onChange={(e) => handleTextInput(e.target.value)}
            className={`${styles.numberInput} text-primary-700 text-[13px] w-full rounded-lg border-[1.5px] border-[#e0d0e2] bg-white py-2 pr-2.5 pl-7 font-semibold tabular-nums outline-none`}
          />
        </div>
      ) : suffix ? (
        <div className="flex w-full">
          <input
            type="number"
            value={value}
            step={step}
            onChange={(e) => handleTextInput(e.target.value)}
            className={`${styles.numberInput} text-primary-700 text-[13px] min-w-0 flex-1 rounded-l-lg border-[1.5px] border-[#e0d0e2] bg-white px-2.5 py-2 font-semibold tabular-nums outline-none`}
          />
          <span className="rounded-r-lg border-[1.5px] border-l-0 border-[#e0d0e2] bg-[#f5f0f6] px-2.25 py-2 text-xs font-bold whitespace-nowrap text-[#9aaac0]">
            {suffix}
          </span>
        </div>
      ) : (
        <input
          type="number"
          value={value}
          step={step}
          onChange={(e) => handleTextInput(e.target.value)}
          className={`${styles.numberInput} text-primary-700 text-[13px] w-full rounded-lg border-[1.5px] border-[#e0d0e2] bg-white px-2.5 py-2 font-semibold tabular-nums outline-none`}
        />
      )}

      <div className="mt-1.25 flex items-center gap-2">
        <span className="text-[9px] font-bold whitespace-nowrap text-[#b0bcd6]">
          {minLabel}
        </span>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className={`${styles.range} h-1 flex-1 cursor-pointer appearance-none rounded-sm border-none p-0 outline-none`}
          style={{
            background: `linear-gradient(to right, #5B245C ${fillPercentage}%, #e4d9e9 ${fillPercentage}%)`,
          }}
        />
        <span className="text-[9px] font-bold whitespace-nowrap text-[#b0bcd6]">
          {maxLabel}
        </span>
      </div>
    </div>
  );
};
